import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { d103Api, FD03, CreateRequest, UpdateRequest } from '../../../services/d103Api';

export type Mode = 'browse' | 'add' | 'edit';

interface FormFields {
  fd03_01: string;
  fd03_02: string;
  fd03_11: string;
  fd03_12: number;
}

const emptyFields: FormFields = {
  fd03_01: '', fd03_02: '', fd03_11: '', fd03_12: 0,
};

export function useD103Form() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<FD03[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [fields, setFields] = useState<FormFields>(emptyFields);

  const current = currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await d103Api.list(1, 1000);
      setRecords(res.data || []);
      return res.data || [];
    } catch {
      message.error('載入失敗');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const syncFields = (rec: FD03) => {
    setFields({
      fd03_01: rec.fd03_01, fd03_02: rec.fd03_02,
      fd03_11: rec.fd03_11, fd03_12: rec.fd03_12,
    });
  };

  useEffect(() => {
    if (current && mode === 'browse') syncFields(current);
  }, [current, mode]);

  useEffect(() => {
    loadRecords().then(data => { if (data.length > 0) setCurrentIdx(0); });
  }, [loadRecords]);

  // Navigation
  const navFirst = () => { if (!isEditing && records.length > 0) setCurrentIdx(0); };
  const navPrev = () => { if (!isEditing && currentIdx > 0) setCurrentIdx(currentIdx - 1); };
  const navNext = () => { if (!isEditing && currentIdx < records.length - 1) setCurrentIdx(currentIdx + 1); };
  const navLast = () => { if (!isEditing && records.length > 0) setCurrentIdx(records.length - 1); };

  const startAdd = () => { setMode('add'); setFields(emptyFields); };
  const startEdit = () => { if (!current) return; setMode('edit'); syncFields(current); };
  const cancel = () => { setMode('browse'); if (current) syncFields(current); };

  const setField = (key: keyof FormFields, val: string | number) => {
    setFields(prev => ({ ...prev, [key]: val }));
  };

  const save = async () => {
    try {
      if (mode === 'add') {
        const req: CreateRequest = { ...fields };
        await d103Api.create(req);
        message.success('新增成功');
      } else {
        const req: UpdateRequest = {
          fd03_02: fields.fd03_02, fd03_11: fields.fd03_11, fd03_12: fields.fd03_12,
        };
        await d103Api.update(current!.fd03_01, req);
        message.success('修改成功');
      }
      const data = await loadRecords();
      const code = mode === 'add' ? fields.fd03_01 : current!.fd03_01;
      const idx = data.findIndex(r => r.fd03_01 === code);
      setCurrentIdx(idx >= 0 ? idx : 0);
      setMode('browse');
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) {
        vErrs.forEach((e: any) => message.error(e.message));
      } else {
        message.error(err?.response?.data?.error || '儲存失敗');
      }
    }
  };

  const doDelete = async () => {
    if (!current) return;
    try {
      await d103Api.delete(current.fd03_01);
      message.success('刪除成功');
      const data = await loadRecords();
      if (data.length > 0) {
        setCurrentIdx(Math.min(currentIdx, data.length - 1));
      } else {
        setCurrentIdx(-1);
      }
    } catch {
      message.error('刪除失敗');
    }
  };

  const quickSearch = (searchText: string) => {
    if (isEditing) return;
    const idx = records.findIndex(r => r.fd03_01 >= searchText);
    if (idx >= 0) setCurrentIdx(idx);
  };

  return {
    mode, isEditing, loading,
    records, current, currentIdx,
    fields, setField,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save,
    doDelete, quickSearch,
  };
}
